import { ListingStatus } from '@apeiron/common'
import { StarFilterInput, StarSpecialFilter } from '@apeiron/star'
import { Box, styled } from '@mui/material'
import {
  FilterCheckboxGroup,
  FilterCheckboxItem
} from '@src/components/planet/filter/filterCheckbox/FilterCheckboxGroup'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import ComponentBlocker from '@src/components/share/generic/ComponentBlocker'
import { ComponentBlock } from '@src/constants/componentBlock'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation, UseTranslationResponse } from 'react-i18next'

const SPECIAL_ITEMS: FilterItem<string>[] = [
  {
    name: 'celestialObject',
    labelKey: 'filter.sections.others.special.celestial',
    value: StarSpecialFilter.celestial
  },
  {
    name: 'ultranova',
    labelKey: 'filter.sections.others.special.ultranova',
    value: StarSpecialFilter.ultranova
  }
]

const SALE_ITEMS: FilterItem<ListingStatus>[] = [
  {
    name: 'forSale',
    labelKey: 'filter.sections.others.status.for_sale',
    value: ListingStatus.LISTING
  },
  {
    name: 'notForSale',
    labelKey: 'filter.sections.others.status.not_for_sale',
    value: ListingStatus.UNLISTING
  }
]

const getCheckboxItems = <T extends unknown>(
  t: UseTranslationResponse<'translation', undefined>['t'],
  items: FilterItem<T>[]
): FilterCheckboxItem<T>[] => {
  return items.map(item => ({
    name: item.name,
    value: item.value,
    label: t(item.labelKey)
  }))
}

type FilterItem<T> = {
  name: string
  labelKey: string
  value: T
}

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding-left: 24px;
    padding-right: 10px;
    padding-top: 8px;
    overflow: hidden;
  }
`

const CheckboxGroupContainer = styled(Box)`
  padding-right: 5px;
`

const StarOthersFilter: FC<Props> = props => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const handleOnValue = useCallback(
    (fields: string[]): string[] => {
      const value: string[] = []

      R.forEach((field: string) => {
        const valueInFilter = R.propOr(null, field, filterInput)

        if (!R.isNil(valueInFilter)) {
          value.push(field)
        }
      }, fields)

      return value
    },
    [filterInput]
  )

  const handleOnReset = useCallback(() => {
    onFilterChange(
      R.omit(
        ['hasCelestialObject', 'hasPermanentNova', 'nftListingStatus'],
        filterInput
      )
    )
  }, [filterInput, onFilterChange])

  const handleOnChange = useCallback(
    (input: string[], fields: string[]) => {
      let newFilterInput = {}

      R.forEach((field: string) => {
        if (R.includes(field, input)) {
          newFilterInput = R.assoc(field, true, newFilterInput)
        } else {
          newFilterInput = R.assoc(field, undefined, newFilterInput)
        }
      }, fields)

      onFilterChange(R.mergeRight(filterInput, newFilterInput))
    },
    [filterInput, onFilterChange]
  )

  const handleOnSaleChange = useCallback(
    (input?: ListingStatus[]) => {
      onFilterChange(R.assoc('nftListingStatus', input, filterInput))
    },
    [filterInput, onFilterChange]
  )

  const specialItems = R.values(StarSpecialFilter)

  const numberOfFilters = useMemo(() => {
    const countStatusFilters = R.pipe<any, any[], number>(
      R.propOr([], 'nftListingStatus'),
      R.length
    )

    const countFilters = (
      input: StarFilterInput,
      fields: (keyof StarFilterInput)[]
    ): number => {
      const count = R.pipe<any[], Array<keyof StarFilterInput>, number>(
        R.reject((field: keyof StarFilterInput) => {
          const value = R.prop(field, input)

          return R.isNil(value)
        }),
        R.length
      )

      return count(fields)
    }

    const numberOfStatusFilters = countStatusFilters(filterInput)

    const numberOfSpecialFilters = countFilters(filterInput, specialItems)

    return numberOfStatusFilters + numberOfSpecialFilters
  }, [filterInput, specialItems])

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>
          {t('star.list.filter.others.title')}
        </FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        <CheckboxGroupContainer>
          <FilterCheckboxGroup
            label={t('star.list.filter.others.special')}
            items={getCheckboxItems(t, SPECIAL_ITEMS)}
            value={handleOnValue(specialItems)}
            onChange={(input: string[]) => handleOnChange(input, specialItems)}
          />
          <ComponentBlocker blockKey={ComponentBlock.StarFilterStatus}>
            <FilterCheckboxGroup
              label={t('filter.sections.others.status.label')}
              items={getCheckboxItems(t, SALE_ITEMS)}
              value={filterInput.nftListingStatus || []}
              onChange={handleOnSaleChange}
            />
          </ComponentBlocker>
        </CheckboxGroupContainer>
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: StarFilterInput
  onFilterChange: (input: StarFilterInput) => void
}

export default StarOthersFilter
